import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useMutation } from "react-query";
import { useParams, useSearchParams } from "react-router-dom";
import useAuthentication from "../../../hooks/useAuthentication";
import usePopup from "../../../hooks/usePopup";
import { EmrContext } from "../../../EMR";
import { voicePromptService } from "../../../services/voiceAssistantService";
import type { ResponseType } from "../../../Types/ServicesType/servicesType";
import type {
  FieldSummaryData,
  VoicePromptRequest,
  VoicePromptResponseBody,
} from "../../../Types/VoiceAssistant/VoiceAssistantTypes";
import {
  EMR_FETCH_PATIENT_SUMMARY_EVENT,
  FIELD_SUMMARY_PROMPT,
} from "./emrPatientSummary";
import { useEmrUrlPatientIds } from "./useEmrUrlPatientIds";

function toPositiveId(value: unknown): number | null {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function parseFieldSummaryData(
  result: ResponseType<VoicePromptResponseBody>,
): FieldSummaryData | null {
  if (result.responseIndicator !== "success") return null;
  const data = result.response?.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data as FieldSummaryData;
  }
  return null;
}

export function useEmrPatientSummary() {
  const { encounterId: routeEncounterId, patientId: routePatientMasterId } =
    useParams();
  const emrIds = useEmrUrlPatientIds();
  const [searchParams] = useSearchParams();
  const { user } = useAuthentication();
  const { showSnackbar } = usePopup();
  const { patientData } = useContext(EmrContext);
  const [summaryData, setSummaryData] = useState<FieldSummaryData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const resolvedEncounterId = useMemo(
    () =>
      emrIds.encounterId ??
      toPositiveId(routeEncounterId) ??
      toPositiveId(patientData?.encounter?.encounterId) ??
      toPositiveId(patientData?.patientDetails?.encounterId),
    [
      emrIds.encounterId,
      patientData?.encounter?.encounterId,
      patientData?.patientDetails?.encounterId,
      routeEncounterId,
    ],
  );

  const resolvedPatientMasterId = useMemo(
    () =>
      emrIds.patientMasterId ??
      toPositiveId(routePatientMasterId) ??
      toPositiveId(patientData?.pmaster?.id) ??
      toPositiveId(patientData?.encounter?.pmasterId) ??
      toPositiveId(patientData?.patientDetails?.pmasterId) ??
      toPositiveId(patientData?.amaster?.patientMasterID),
    [
      emrIds.patientMasterId,
      patientData?.amaster?.patientMasterID,
      patientData?.encounter?.pmasterId,
      patientData?.patientDetails?.pmasterId,
      patientData?.pmaster?.id,
      routePatientMasterId,
    ],
  );

  const hasPatientContext = Boolean(resolvedEncounterId && resolvedPatientMasterId);

  const buildPayload = useCallback((): VoicePromptRequest => {
    const emrNo =
      patientData?.pmaster?.patientId?.toString() ||
      emrIds.patientId ||
      searchParams.get("emrno")?.trim() ||
      null;

    return {
      date: dayjs().format("YYYY-MM-DD"),
      prompt: FIELD_SUMMARY_PROMPT,
      type: "field-summary",
      language: navigator.language || null,
      userId: user?.userId ?? user?.login_id ?? null,
      encounterId: resolvedEncounterId,
      patientMasterId: resolvedPatientMasterId,
      patientId: emrNo,
    };
  }, [
    emrIds.patientId,
    patientData?.pmaster?.patientId,
    resolvedEncounterId,
    resolvedPatientMasterId,
    searchParams,
    user?.login_id,
    user?.userId,
  ]);

  const { mutate: fetchSummary, isLoading } = useMutation(voicePromptService, {
    onSuccess: (result) => {
      const data = parseFieldSummaryData(result);
      if (!data) {
        showSnackbar({
          open: true,
          message: result.responseMessage || "Could not load patient summary.",
          variant: "warning",
          duration: 5000,
        });
        return;
      }
      setSummaryData(data);
      setModalOpen(true);
    },
    onError: (error: {
      message?: string;
      response?: { data?: { responseMessage?: string } };
    }) => {
      showSnackbar({
        open: true,
        message:
          error?.response?.data?.responseMessage ||
          error?.message ||
          "Failed to load patient summary.",
        variant: "error",
        duration: 5000,
      });
    },
  });

  const handleFetchSummary = useCallback(() => {
    if (!hasPatientContext) {
      showSnackbar({
        open: true,
        message: "Patient encounter context is missing.",
        variant: "warning",
        duration: 4000,
      });
      return;
    }
    fetchSummary(buildPayload());
  }, [buildPayload, fetchSummary, hasPatientContext, showSnackbar]);

  useEffect(() => {
    const onFetchRequest = () => {
      handleFetchSummary();
    };

    window.addEventListener(EMR_FETCH_PATIENT_SUMMARY_EVENT, onFetchRequest);
    return () =>
      window.removeEventListener(EMR_FETCH_PATIENT_SUMMARY_EVENT, onFetchRequest);
  }, [handleFetchSummary]);

  return {
    hasPatientContext,
    summaryData,
    modalOpen,
    setModalOpen,
    isLoading,
    handleFetchSummary,
  };
}
